import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import config from '../../../../config';
import Translate, {t} from '../../../../components/Translate';
import DonutChart from '../../../../components-new/general/charts/DonutChart';
import BarsChart from '../../../../components-new/general/charts/BarsChart';
import useChartPalette, {platformColor}
    from '../../../../components-new/general/charts/palette';
import {buildHomeLinks} from './links';
import StatisticCard from '../../../../components-new/general/statistics/StatisticCard';
import SilverButton from '../../../../components-new/buttons/SilverButton';
import '../../../../styles/settings-layout.css';
import {
    getBotStatisticNumbers,
    getOperatorOfTheMonth,
    getOperatorOfTheMonthHistory,
    getOperatorsTableData,
    getStatisticBotNumbers,
    getStatisticNumbers
} from '../../../../services/statistics/api';
import {formatSecondsToHHMMSS, hasManagementPermission} from '../../../../utils/General';
import Colors from '../../../../utils/Colors';
import Tooltip from '../../../../components-new/general/Tooltip';
import ForumIcon from '../../../../assets/icons/ForumIcon';
import ColorIndicator from '../../../../components-new/general/ColorIndicator';
import ContactAvatar from '../../../../components-new/general/ContactAvatar';
import OperatorProfileLink from '../../../../components-new/general/OperatorProfileLink';
import OperatorProfileDrawer from '../../../../components-new/general/OperatorProfileDrawer';
import LevelBadge from '../../../../components-new/general/LevelBadge';
import RatingStars from '../../../../components-new/general/RatingStars';
import CheckIcon from '../../../../assets/icons/CheckIcon';
import ClockArrowDownIcon from '../../../../assets/icons/ClockArrowDownIcon';
import EmployeesIcon from '../../../../assets/icons/nav/EmployeesIcon';

// the "right now" numbers go stale within a minute of being read
const REFRESH_MS = 30000;

const STATISTICS_PATH = '/dashboard/management/statistics';

/* the backend answers with a month number; these are the words for it */
const MONTH_KEYS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
];

/*
 * Who counts as top, and in what order.
 *
 * Both questions are answered by the server now - see OperatorScoring in
 * chat-backend. isRanked is whether the operator has done enough work for their
 * figures to mean anything, and score is the same hundred points the level badge
 * is a band of.
 *
 * Sorting by that score rather than by rating is what stops this list
 * contradicting itself. It used to sort by rating alone while printing a badge
 * worked out from three conditions, so level 8 could sit below level 2 - two
 * different measurements put side by side have no reason to agree. One
 * measurement, and the order and the badges cannot disagree.
 *
 * The floors are no longer numbers written here either. They follow the size of
 * the company: half of what an average operator handled, which is a real bar in
 * a company of eighty and an achievable one in a company of three. Five hundred
 * chats simply meant small companies had no top operators at all.
 */
const TOP_OPERATORS_SHOWN = 5;

const EMPTY_NUMBERS = {
    allRequests: 0,
    todaysRequests: 0,
    answeredRequests: 0,
    unansweredRequests: 0,
    activeRequests: 0,
    forwardedRequests: 0,
};

function ManagerHome({user, company}) {
    const subs = company.subs ? company.subs : [];

    /*
     * One array, not a new one per render. The figures on this page refresh on a
     * timer, and everything derived from a fresh array downstream - a memo, an
     * effect, a child's fetch - woke up with them.
     */
    const subIds = useMemo(
        () => subs.map(sub => sub.id),
        [subs.map(sub => sub.id).join(',')]
    );

    const [numbers, setNumbers] = useState(EMPTY_NUMBERS);
    const [loadingNumbers, setLoadingNumbers] = useState(true);

    const [botNumbers, setBotNumbers] = useState({});

    /*
     * One row per bot with a count per platform - the same figures the table in
     * Statistics is built from. Both rings below are sums of it, read in the two
     * directions: down the columns for the platforms, across the rows for the bots.
     */
    const [botPlatformRows, setBotPlatformRows] = useState([]);
    const [topOperators, setTopOperators] = useState(null);
    const [operatorOfMonth, setOperatorOfMonth] = useState(null);

    /* whose profile is open, or null - the drawer is shared by both lists below */
    const [operatorProfile, setOperatorProfile] = useState(null);

    /* scoped to this manager's bots, and built once - see the note on subIds */
    const profileFilters = useMemo(() => ({companyIds: subIds}), [subIds]);

    /* the months before this one, fetched once and shown on request */
    const [awardHistory, setAwardHistory] = useState([]);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    /*
     * Without the month the card above is already showing. The server answers
     * with every month it has, which is the honest thing for it to do; printing
     * the current one twice on one card is not.
     */
    const earlierMonths = useMemo(() => {
        if (!operatorOfMonth) return awardHistory;

        return awardHistory.filter(
            month => !(month.year === operatorOfMonth.year && month.month === operatorOfMonth.month)
        );
    }, [awardHistory, operatorOfMonth]);
    const canViewOperatorProfile = hasManagementPermission(user);

    /*
     * From the store, not from the endpoint.
     *
     * fetchCompanyMembers is the auth service's /users, and currentStatus is not
     * in it - the saga merges that in from the chat backend before putting the
     * list in the store. Calling the endpoint here got records with no status at
     * all, so every operator counted as unavailable and the tile read 0 forever.
     */
    const team = useMemo(() => {
        const live = (company.members || []).filter(m => m.status !== config.statusMap.deleted && m.authorities);

        const count = (roles) => {
            const members = live.filter(m => roles.some(role => m.authorities.indexOf(role) !== -1));
            return {
                available: members.filter(m => m.currentStatus === config.currentStatusMap.available).length,
                total: members.length,
            };
        };

        return {
            agents: count([config.rolesMap.agent]),
            // an owner is an admin with the billing on top, and somebody asking
            // "is anyone in charge around" means both
            admins: count([config.rolesMap.admin]),
        };
    }, [company.members]);

    useEffect(() => {
        let active = true;

        const fetchNumbers = () => {
            getStatisticNumbers(subIds)
                .then(result => {
                    if (active && result) setNumbers(result);
                })
                .finally(() => {
                    if (active) setLoadingNumbers(false);
                });
        };

        fetchNumbers();
        const interval = setInterval(fetchNumbers, REFRESH_MS);

        return () => {
            active = false;
            clearInterval(interval);
        };
    }, [subIds]);

    useEffect(() => {
        let active = true;

        getStatisticBotNumbers(subIds).then(result => {
            if (active && result) setBotNumbers(result);
        });

        getBotStatisticNumbers({companyIds: subIds, start: null, end: null}, {sort: ''})
            .then(rows => {
                if (active) setBotPlatformRows(rows || []);
            })
            .catch(() => {
                if (active) setBotPlatformRows([]);
            });

        getOperatorOfTheMonth(company.id, subIds)
            .then(winner => {
                if (active) setOperatorOfMonth(winner);
            })
            .catch(() => {
                if (active) setOperatorOfMonth(null);
            });

        getOperatorOfTheMonthHistory(company.id)
            .then(months => {
                if (active) setAwardHistory(months || []);
            })
            .catch(() => {
                if (active) setAwardHistory([]);
            });

        getOperatorsTableData(company.id, {companyIds: subIds, start: null, end: null}, {sort: ''})
            .then(rows => {
                if (!active) return;

                const ranked = (rows || [])
                    .filter(row => row.isRanked)
                    .sort((a, b) => {
                        const byScore = (Number(b.score) || 0) - (Number(a.score) || 0);
                        // the same score from more conversations is the stronger
                        // of the two, so it goes first
                        return byScore || (Number(b.answeredChats) || 0) - (Number(a.answeredChats) || 0);
                    })
                    .slice(0, TOP_OPERATORS_SHOWN);

                setTopOperators(ranked);
            })
            .catch(() => {
                if (active) setTopOperators([]);
            });

        return () => {
            active = false;
        };
    }, [subIds]);

    /*
     * How much of the bot's traffic it handled by itself. This is the only
     * number on the page that says whether the bot is earning its keep, which is
     * why it is given as a share rather than as two counts to compare by eye.
     */
    const botAll = Number(botNumbers.allRequests) || 0;
    const botSolved = Number(botNumbers.solvedByBotRequests) || 0;
    const botShare = botAll ? Math.round((botSolved / botAll) * 100) : null;

    const palette = useChartPalette();

    /*
     * Answered against unanswered, and nothing else in the ring.
     *
     * These two are the one true split the backend offers: both are counted off
     * the same table by the same flag, so they add up to allRequests exactly.
     * Active and forwarded look like they belong here and do not - active is
     * read from the live conversations table, forwarded is a subset of the
     * answered - and either of them would make a total that matches no tile on
     * the page.
     */
    const answeredSlices = useMemo(() => [
        {
            key: 'answered',
            name: t({text: 'answered', user}),
            value: Number(numbers.answeredRequests) || 0,
            color: palette.positive,
        },
        {
            key: 'unanswered',
            name: t({text: 'unanswered', user}),
            value: Number(numbers.unansweredRequests) || 0,
            color: palette.negative,
        },
    ], [numbers.answeredRequests, numbers.unansweredRequests, palette, user]);

    /* only the channels this company actually sells, in the tariff's own order */
    const messengers = useMemo(
        () => (company.tariff && company.tariff.messengers) ? company.tariff.messengers : [],
        [company.tariff]
    );

    const platformSlices = useMemo(() => messengers.map((messenger, index) => {
        const key = messenger.toLowerCase();

        return {
            key: key,
            name: messenger.charAt(0).toUpperCase() + messenger.slice(1).toLowerCase(),
            value: botPlatformRows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
            color: platformColor(palette, key, index),
        };
    }), [botPlatformRows, messengers, palette]);

    /*
     * The busiest bots, as a ranking rather than as a third ring.
     *
     * Bars because it is a different question from the two beside it: those two
     * are "what is this made of", where a ring is the right shape, and this one
     * is "who is biggest", where the answer is an order. A ring also could not
     * hold it - forty bot names around a circle is unreadable, and cutting it to
     * six would leave a total in the middle that no longer matched its own
     * slices. A top six makes no claim about a total.
     */
    const botBars = useMemo(() => botPlatformRows
        .map(row => ({
            name: row.name,
            count: messengers.reduce(
                (sum, messenger) => sum + (Number(row[messenger.toLowerCase()]) || 0), 0
            ),
            color: palette.series1,
        }))
        .filter(row => row.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 6),
        [botPlatformRows, messengers, palette]);

    const links = buildHomeLinks(user, company);
    const canOpenStatistics = links.some(link => link.path === STATISTICS_PATH);

    return (
        <div className="stg-page">
            <h1 className="home-greeting">
                <Translate text="dashboardGreeting"/>{user.firstName ? ', ' + user.firstName : ''}
            </h1>

            {/*
                One row, and no captions over it.

                "Right now" and "Today" were two headings for four tiles, and
                each tile already says what it counts - the second heading was
                there to explain a single card. Splitting them also split the
                row: three tiles stretched across half the page while one sat
                alone in the other half.

                The two request figures first, then the two head-counts.
            */}
            <div className="stat-card-group">
                <StatisticCard
                    title="activeRequests"
                    value={numbers.activeRequests}
                    loading={loadingNumbers}
                    icon={<ForumIcon style={{color: Colors.blueDark}}/>}
                />
                <StatisticCard
                    title="todaysRequestsToOperator"
                    value={numbers.todaysRequests}
                    loading={loadingNumbers}
                    icon={<CheckIcon style={{color: Colors.green}}/>}
                />
                {/* the availability dot rather than a head-count icon: what
                    these two tiles count is who is AVAILABLE, and that is the
                    mark the member list and My Space use for it */}
                <StatisticCard
                    title="dashboardOperatorsAvailable"
                    value={team.agents.total ? team.agents.available + ' / ' + team.agents.total : '—'}
                    icon={<ColorIndicator currentStatus={config.currentStatusMap.available}/>}
                    iconBare
                />
                <StatisticCard
                    title="dashboardAdminsAvailable"
                    value={team.admins.total ? team.admins.available + ' / ' + team.admins.total : '—'}
                    icon={<ColorIndicator currentStatus={config.currentStatusMap.available}/>}
                    iconBare
                />
            </div>

            {/* The two standing blocks, beside each other when both are here
                and each across the page when the other is not. auto-fit is what
                does that: .stg-grid is always two columns, and a lone block in it
                sits at half the width with an empty half beside it. */}
            {/*
                Absent when nobody earned it - the backend answers 204 rather than
                a winner with no name - so a quiet month leaves no empty frame.
            */}
            {operatorOfMonth && (
                <div className="home-award">
                    <span className="home-award__mark" aria-hidden="true">🏆</span>

                    <div className="home-award__body">
                        <div className="home-award__head">
                            <span className="home-award__title">
                                <Translate text="dashboardOperatorOfMonth"/>
                            </span>
                            <span className="home-award__period">
                                {MONTH_KEYS[operatorOfMonth.month - 1] && (
                                    <Translate text={MONTH_KEYS[operatorOfMonth.month - 1]}/>
                                )} {operatorOfMonth.year}
                            </span>
                        </div>

                        <div className="home-award__winner">
                            {/* the award answers with operatorId where every other
                                list answers with id, and the drawer asks for an
                                operator, not for this particular shape of one */}
                            <OperatorProfileLink
                                operator={{...operatorOfMonth, id: operatorOfMonth.operatorId}}
                                canOpen={canViewOperatorProfile}
                                onOpen={setOperatorProfile}
                            >
                                <ContactAvatar contact={operatorOfMonth} size={40}/>
                                <span className="home-award__name">
                                    {operatorOfMonth.firstName} {operatorOfMonth.lastName}
                                </span>
                            </OperatorProfileLink>
                        </div>

                        <div className="home-award__figures">
                            <span>
                                <Translate text="answeredRequestsToOperator"/>
                                <b>{operatorOfMonth.answeredChats}</b>
                            </span>
                            <span>
                                <Translate text="dashboardRatingScore"/>
                                <b>{operatorOfMonth.rating}%</b>
                            </span>
                            {operatorOfMonth.avgResponseTime !== null && (
                                <span>
                                    <Translate text="avrgAnswerSpeed"/>
                                    <b>{formatSecondsToHHMMSS(operatorOfMonth.avgResponseTime)}</b>
                                </span>
                            )}
                        </div>

                        {/*
                            The months before this one.

                            Folded away rather than listed: the award is about who
                            holds the title now, and a column of past winners under
                            it would say the opposite. It is here for the one
                            question it answers - has this been the same person all
                            year - and that question is asked rarely enough to be
                            worth a click.

                            Only when there is a month before this one to show: the
                            first month a company uses this, the button would open
                            on nothing.
                        */}
                        {earlierMonths.length > 0 && (
                            <div className="home-award__history">
                                <button
                                    type="button"
                                    className="home-award__history-toggle"
                                    onClick={() => setIsHistoryOpen(open => !open)}
                                >
                                    <Translate text={isHistoryOpen
                                        ? 'dashboardAwardHistoryHide'
                                        : 'dashboardAwardHistory'}/>
                                </button>

                                {/*
                                    Always here, open or shut.

                                    A list that is unmounted has no leaving to do:
                                    it was gone in the frame the button was
                                    pressed. Kept with no height instead, which is
                                    a height that can be transitioned.
                                */}
                                <div className={'home-award__history-list'
                                    + (isHistoryOpen ? ' is-open' : '')}
                                     aria-hidden={!isHistoryOpen}>
                                    {earlierMonths.map(month => (
                                        <div className="home-award__history-row"
                                             key={month.year + '-' + month.month}>
                                            <span className="home-award__history-period">
                                                {MONTH_KEYS[month.month - 1] && (
                                                    <Translate text={MONTH_KEYS[month.month - 1]}/>
                                                )} {month.year}
                                            </span>

                                            {/* one cell whatever happens inside it:
                                                without the permission the link steps
                                                aside and hands its children straight
                                                through, and two of them in a row of
                                                three columns is a row that no longer
                                                lines up */}
                                            <span className="home-award__history-name">
                                                <OperatorProfileLink
                                                    operator={{...month, id: month.operatorId}}
                                                    canOpen={canViewOperatorProfile}
                                                    onOpen={setOperatorProfile}
                                                >
                                                    <ContactAvatar contact={month} size={24}/>
                                                    <span>{month.firstName} {month.lastName}</span>
                                                </OperatorProfileLink>
                                            </span>

                                            {/* two numbers with a slash between
                                                them say nothing on their own; the
                                                same two words the card above uses
                                                for them say which is which */}
                                            <span className="home-award__history-figure">
                                                <Tooltip text="answeredRequestsToOperator">
                                                    {month.answeredChats}
                                                </Tooltip>
                                                <span className="home-award__history-slash">/</span>
                                                <Tooltip text="dashboardRatingScore">
                                                    {month.rating}%
                                                </Tooltip>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/*
                Three charts, not three rows of numbers. They sit here rather than
                in Statistics because the page is already a grid of equal blocks -
                in Statistics each one had a full-width section to itself, which
                for a ring of two slices is a great deal of white.
            */}
            <div className="home-charts">
                <DonutChart
                    titleKey="answeredShare"
                    totalLabelKey="allRequestsToOperator"
                    data={answeredSlices}
                />

                <DonutChart
                    titleKey="requestsByPlatform"
                    totalLabelKey="allRequestsToOperator"
                    data={platformSlices}
                />

                <BarsChart
                    titleKey="requestsByBot"
                    data={botBars}
                    series={[{key: 'count', name: t({text: 'allRequestsToOperator', user})}]}
                    layout="vertical"
                    categoryWidth={110}
                />
            </div>

            <div className="home-pair">
                <div className="home-section">
                    <h2 className="stg-card__title"><Translate text="dashboardBots"/></h2>

                    <div className="stg-list">
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="dashboardBotSolvedShare"/></span>
                            <span className="stg-row__actions home-figure">
                                {botShare === null ? '—' : botShare + '%'}
                            </span>
                        </div>
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="all"/></span>
                            <span className="stg-row__actions home-figure">{botAll}</span>
                        </div>
                        <div className="stg-row">
                            <span className="stg-row__text"><Translate text="transferredToOperatorsCount"/></span>
                            <span className="stg-row__actions home-figure">
                                {Number(botNumbers.transferredToOperatorRequests) || 0}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="home-section">
                    <div className="setting-card__heading">
                        <h2 className="stg-card__title"><Translate text="dashboardTopOperators"/></h2>
                    </div>

                    <div className="stg-list">
                        {topOperators === null && <div className="stg-empty">...</div>}

                        {topOperators !== null && !topOperators.length && (
                            <div className="stg-empty"><Translate text="dashboardTopOperatorsEmpty"/></div>
                        )}

                        {(topOperators || []).map(operator => (
                            <div className="stg-row" key={operator.id}>
                                <OperatorProfileLink
                                    operator={operator}
                                    canOpen={canViewOperatorProfile}
                                    onOpen={setOperatorProfile}
                                    className="stg-row__text statistics-operator-cell"
                                >
                                    <ContactAvatar contact={operator} size={32}/>
                                    <span>{operator.firstName} {operator.lastName}</span>
                                </OperatorProfileLink>
                                {/* Four figures, each on a tooltip rather than under
                                    a label: the row is half a page wide and four
                                    captions would leave no room for the name. This
                                    is the same way the operators table in
                                    Statistics shows the very same numbers. */}
                                <div className="stg-row__actions">
                                    <Tooltip text="answeredRequestsToOperator" style={{display: 'flex'}}>
                                        <span className="home-figure">{operator.answeredChats}</span>
                                    </Tooltip>

                                    <RatingStars
                                        value={((Number(operator.rating) || 0) * 5) / 100}
                                        starSize="small"
                                    />
                                    <span className="home-figure">{operator.rating}%</span>

                                    <Tooltip text="ratingCount" style={{display: 'flex'}}>
                                        <span className="home-aside">{operator.ratingCount}</span>
                                    </Tooltip>

                                    {/* just the word: the badge is one of four
                                        figures in the row, and all the tooltip has
                                        to answer is which one it is. The rule behind
                                        levels is on the heading's own hint. */}
                                    <Tooltip text="Level" style={{display: 'flex'}}>
                                        <LevelBadge level={operator.level}/>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="setting-cards">
                {links.map(link => {
                    const Icon = link.icon;
                    return (
                        <Link className="home-quick-link" key={link.key} to={link.path}>
                            <SilverButton asDiv variant="outline" width="auto" height="36px">
                                <Icon width={16} height={16}/>
                                <Translate text={link.label}/>
                            </SilverButton>
                        </Link>
                    );
                })}
            </div>

            {/* Only where there is a Statistics to open. The sentence is tied to
                the link rather than to a permission of its own, so it cannot
                outlive what it points at. */}
            {canOpenStatistics && (
                <div className="stg-empty home-hint">
                    <ClockArrowDownIcon width={14} height={14}/>
                    <Translate text="dashboardPeriodHint"/>
                </div>
            )}

            {/*
                Scoped to the manager's own bots and left without dates on
                purpose. Both lists above are read over the whole of an
                operator's time - the award is decided on last month but it is
                the person being opened, not the month - so a profile narrowed to
                a period nobody asked for would disagree with the row that opened
                it.
            */}
            <OperatorProfileDrawer
                open={!!operatorProfile}
                onClose={() => setOperatorProfile(null)}
                operator={operatorProfile}
                parentCompanyId={company.id}
                filters={profileFilters}
            />
        </div>
    );
}

export default ManagerHome;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/home/ManagerHome.jsx